import { type CalculatorDef, type CalcResult, checkFinite, err, num, ok } from './types';

export type DilutionResult = {
  /** Litres of water to add. */
  waterToAdd: number;
  /** Litres in the vessel once the water is in. */
  finalVolume: number;
  /** Litres of pure ethanol — unchanged by dilution, shown as a sanity check. */
  absoluteAlcohol: number;
}

/**
 * How much water to add to bring a spirit down to a target strength.
 *
 * Adding water changes the volume but not the amount of ethanol, so the ethanol content
 * is the invariant:
 *
 *     volume x abv = finalVolume x targetAbv
 *     finalVolume  = volume x abv / targetAbv
 *
 * Assumes ideal mixing. In reality ethanol and water contract when combined — the true
 * final volume is slightly *less* than the sum of the parts, peaking near 50% ABV — so
 * this slightly under-states the water needed, by up to roughly 2-3%. Distillers normally
 * add a little under the calculated amount, measure, and top up. Do not use this for
 * excise declarations.
 *
 * @param volume   Litres of spirit you have.
 * @param abv      Its current strength, %ABV.
 * @param targetAbv Strength you want, %ABV.
 */
export function dilute(volume: number, abv: number, targetAbv: number): CalcResult<DilutionResult> {
  const invalid = checkFinite({ volume, abv, targetAbv });
  if (invalid) return { ok: false, error: invalid };

  if (volume <= 0) return err('error.volumePositive', undefined, 'volume');
  if (abv <= 0 || abv > 100) return err('error.abvRange', undefined, 'abv');
  if (targetAbv <= 0 || targetAbv > 100) return err('error.abvRange', undefined, 'targetAbv');
  if (targetAbv > abv) return err('error.targetAboveSource', { abv }, 'targetAbv');

  const absoluteAlcohol = (volume * abv) / 100;
  const finalVolume = (volume * abv) / targetAbv;

  return ok({
    waterToAdd: finalVolume - volume,
    finalVolume,
    absoluteAlcohol,
  });
}

export const dilutionCalculator: CalculatorDef = {
  id: 'dilution',
  notesKey: 'notes.idealMixing',
  fields: [
    { name: 'volume', unit: 'litre', min: 0.01, max: 10000, step: 0.1, defaultValue: 10 },
    { name: 'abv', unit: 'percent', min: 0.1, max: 100, step: 0.1, defaultValue: 70 },
    { name: 'targetAbv', unit: 'percent', min: 0.1, max: 100, step: 0.1, defaultValue: 50 },
  ],
  outputs: [
    { name: 'waterToAdd', unit: 'litre', precision: 2, primary: true },
    { name: 'finalVolume', unit: 'litre', precision: 2 },
    { name: 'absoluteAlcohol', unit: 'litre', precision: 2 },
  ],
  compute: (i) => dilute(num(i, 'volume'), num(i, 'abv'), num(i, 'targetAbv')),
};
